import { createSlice } from '@reduxjs/toolkit';

import { calendarApiV4 } from 'api';
import { transformFiltersToQuery } from '../utils/transformFilterToQuery';

export const calendarSlice = createSlice({
    name: 'calendarV4',
    initialState: {
        pending: false,
        savePending: false,
        calendars: [],
        error: null,
        deletePending: false,
        deleteError: null,
        deleteId: null,
        count: 0,
        total: 1,
        currentPage: 1,
        totalPages: 1,
    },
    reducers: {
        start: (state, action) => {
            state.pending = true;
        },
        startSave: (state, action) => {
            state.savePending = true;
        },
        startDelete: (state, action) => {
            state.deletePending = true;
            state.deleteId = action.payload;
        },
        stopDelete: (state, action) => {
            state.deletePending = false;
        },
        stop: (state, action) => {
            state.pending = false;
        },
        stopSave: (state, action) => {
            state.savePending = false;
        },
        addCalendar: (state, action) => {
            state.savePending = false;
            state.calendars = [action.payload, ...state.calendars];
        },
        editCalendar: (state, action) => {
            state.savePending = false;
            const index = state.calendars.findIndex((calendar) => calendar.id === action.payload.id);
            if (index !== -1) {
                state.calendars = [
                    ...state.calendars.slice(0, index),
                    action.payload,
                    ...state.calendars.slice(index + 1),
                ];
            }
        },
        deleteCalendar: (state, action) => {
            state.deletePending = false;
            const index = state.calendars.findIndex((calendar) => calendar.id === action.payload.id);
            if (index !== -1) {
                state.calendars = [...state.calendars.slice(0, index), ...state.calendars.slice(index + 1)];
            }
        },
        setCalendars: (state, action) => {
            state.pending = false;
            state.count = action.payload.count;
            state.total = action.payload.total;
            state.calendars = action.payload.items;
            state.currentPage = action.payload.currentPage;
            state.totalPages = action.payload.totalPages;
        },
        setError: (state, action) => {
            state.pending = false;
            state.error = action.payload;
        },
        setSaveError: (state, action) => {
            state.savePending = false;
            state.error = action.payload;
        },
    },
});

const {
    start,
    stop,
    setCalendars,
    stopDelete,
    setError,
    addCalendar,
    editCalendar,
    deleteCalendar,
    startDelete,
    startSave,
    stopSave,
    setSaveError,
} = calendarSlice.actions;

export const getCalendars = (state) => state.calendarV4.calendars;
export const getCalendarsPending = (state) => state.calendarV4.pending;
export const getCalendarsSavePending = (state) => state.calendarV4.savePending;
export const getCalendarDeletePending = (state) => state.calendarV4.deletePending;
export const getCalendarDeleteId = (state) => state.calendarV4.deleteId;
export const getCalendarsError = (state) => state.calendarV4.error;
export const getCalendarsTotal = (state) => state.calendarV4.total;
export const getCalendarsCurrentPage = (state) => state.calendarV4.currentPage;
export const getCalendarsTotalPages = (state) => state.calendarV4.totalPages;

export const fetchCalendarsAction =
    ({ queryParams, onSuccess, onFailure, options } = {}) =>
    async (dispatch) => {
        const shouldUseState = options?.useState ?? true;
        try {
            let query = transformFiltersToQuery(queryParams);
            if (shouldUseState) {
                dispatch(start());
            }
            calendarApiV4
                .get('calendars' + query)
                .then((response) => {
                    if (shouldUseState) {
                        dispatch(setCalendars(response.data));
                    }
                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                    return response;
                })
                .catch((error) => {
                    if (shouldUseState) {
                        dispatch(setError(error));
                    }
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            if (shouldUseState) {
                dispatch(stop());
            }
            return console.error(e.message);
        }
    };

export const addCalendarAction =
    ({ calendarDto, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(startSave());
            calendarApiV4
                .post('calendars', calendarDto)
                .then((response) => {
                    dispatch(addCalendar(response?.data));
                    onSuccess?.(response?.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setSaveError(error));
                    onFailure?.(error);
                });
        } catch (e) {
            dispatch(stopSave());
            onFailure?.(e);
            return console.error(e.message);
        }
    };

export const editCalendarAction =
    ({ id, calendarDto, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(startSave());
            calendarApiV4
                .put('calendars/' + id, calendarDto)
                .then((response) => {
                    dispatch(editCalendar(response.data));
                    if (onSuccess) {
                        onSuccess(null);
                    }
                    return response;
                })
                .catch((error) => {
                    dispatch(setSaveError(error));
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            dispatch(stopSave());
            return console.error('error', e);
        }
    };

export const deleteCalendarAction = (id, onSuccess, onFailure) => async (dispatch) => {
    try {
        dispatch(startDelete(id));
        calendarApiV4
            .delete('calendars/' + id)
            .then((response) => {
                dispatch(deleteCalendar(response.data));
                if (onSuccess) {
                    onSuccess();
                }
                return response;
            })
            .catch((error) => {
                dispatch(setError(error));
                if (onFailure) {
                    onFailure();
                }
            });
    } catch (e) {
        dispatch(stopDelete());
        return console.error('error', e);
    }
};

export default calendarSlice.reducer;
